/**
 * Route Optimizer Sidebar — Hierarchical navigation: Companies → Branches → Weeks.
 * Mirrors the localhost:5000 DemoWebGUI sidebar layout.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as api from '../services/routeOptimizerService';
import '../styles/ROSidebar.css';

export default function ROSidebar() {
  const navigate = useNavigate();
  const location = useLocation();

  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [addingCompany, setAddingCompany] = useState(false);
  const [addingBranch, setAddingBranch] = useState(false);
  const [addingWeek, setAddingWeek] = useState(false);
  const [newName, setNewName] = useState('');

  // Load companies on mount
  const loadCompanies = useCallback(async () => {
    try {
      const data = await api.listCompanies();
      setCompanies(data);
    } catch (err) {
      console.error('Failed to load companies:', err);
    }
  }, []);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  // Sync selection from URL
  useEffect(() => {
    const path = location.pathname;
    const branchMatch = path.match(/\/routeoptimizer\/branch\/([^/]+)/);
    const weekMatch = path.match(/\/routeoptimizer\/week\/([^/]+)/);
    if (weekMatch) {
      setSelectedWeek(weekMatch[1]);
    } else if (branchMatch) {
      setSelectedBranch(branchMatch[1]);
      setSelectedWeek(null);
    }
  }, [location.pathname]);

  // Load branches when company selected
  const selectCompany = useCallback(async (cid) => {
    setSelectedCompany(cid);
    setSelectedBranch(null);
    setSelectedWeek(null);
    setBranches([]);
    setWeeks([]);
    try {
      const data = await api.listBranches(cid);
      setBranches(data);
      // If only one branch, auto-select it
      if (data.length === 1) {
        selectBranch(data[0].id);
      }
    } catch (err) {
      console.error('Failed to load branches:', err);
    }
  }, []);

  // Load weeks when branch selected
  const selectBranch = useCallback(async (bid) => {
    setSelectedBranch(bid);
    setSelectedWeek(null);
    setWeeks([]);
    try {
      const resp = await api.listWeeks(bid);
      setWeeks(resp.weeks || []);
    } catch (err) {
      console.error('Failed to load weeks:', err);
    }
    navigate(`/routeoptimizer/branch/${bid}`);
  }, [navigate]);

  const selectWeek = useCallback((wid) => {
    setSelectedWeek(wid);
    navigate(`/routeoptimizer/week/${wid}`);
  }, [navigate]);

  // Auto-expand from URL on initial load
  useEffect(() => {
    if (companies.length === 0) return;
    const path = location.pathname;
    const weekMatch = path.match(/\/routeoptimizer\/week\/([^/]+)/);
    const branchMatch = path.match(/\/routeoptimizer\/branch\/([^/]+)/);

    // We need to find which company/branch owns this week
    const findParents = async () => {
      if (weekMatch) {
        // Load week details to find parent
        try {
          const details = await api.getWeekDetails(weekMatch[1]);
          if (details.company_id && details.branch_id) {
            setSelectedCompany(details.company_id);
            const branchData = await api.listBranches(details.company_id);
            setBranches(branchData);
            setSelectedBranch(details.branch_id);
            const weekResp = await api.listWeeks(details.branch_id);
            setWeeks(weekResp.weeks || []);
            setSelectedWeek(weekMatch[1]);
          }
        } catch (err) {
          console.error('Failed to resolve week parents:', err);
        }
      } else if (branchMatch) {
        // Find which company owns this branch
        for (const c of companies) {
          try {
            const branchData = await api.listBranches(c.id);
            const found = branchData.find(b => b.id === branchMatch[1]);
            if (found) {
              setSelectedCompany(c.id);
              setBranches(branchData);
              setSelectedBranch(branchMatch[1]);
              const weekResp = await api.listWeeks(branchMatch[1]);
              setWeeks(weekResp.weeks || []);
              break;
            }
          } catch { /* skip */ }
        }
      }
    };
    findParents();
  }, [companies, location.pathname]);

  // Add handlers
  const handleAddCompany = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    await api.createCompany(newName.trim());
    setNewName('');
    setAddingCompany(false);
    await loadCompanies();
  };

  const handleAddBranch = async (e) => {
    e.preventDefault();
    if (!newName.trim() || !selectedCompany) return;
    await api.createBranch(selectedCompany, { name: newName.trim() });
    setNewName('');
    setAddingBranch(false);
    const data = await api.listBranches(selectedCompany);
    setBranches(data);
  };

  const handleAddWeek = async (e) => {
    e.preventDefault();
    if (!newName.trim() || !selectedBranch) return;
    await api.createWeek(selectedBranch, newName.trim());
    setNewName('');
    setAddingWeek(false);
    const resp = await api.listWeeks(selectedBranch);
    setWeeks(resp.weeks || []);
  };

  const handleDeleteCompany = async (e, cid) => {
    e.stopPropagation();
    if (!window.confirm('Delete this company and all its data?')) return;
    await api.deleteCompany(cid);
    if (selectedCompany === cid) {
      setSelectedCompany(null);
      setBranches([]);
      setWeeks([]);
      navigate('/routeoptimizer');
    }
    await loadCompanies();
  };

  const handleDeleteBranch = async (e, bid) => {
    e.stopPropagation();
    if (!window.confirm('Delete this branch and all its data?')) return;
    await api.deleteBranch(bid);
    if (selectedBranch === bid) {
      setSelectedBranch(null);
      setWeeks([]);
    }
    const data = await api.listBranches(selectedCompany);
    setBranches(data);
  };

  const handleDeleteWeek = async (e, wid) => {
    e.stopPropagation();
    if (!window.confirm('Delete this week and all its data?')) return;
    await api.deleteWeek(wid);
    if (selectedWeek === wid) {
      setSelectedWeek(null);
      navigate(`/routeoptimizer/branch/${selectedBranch}`);
    }
    const resp = await api.listWeeks(selectedBranch);
    setWeeks(resp.weeks || []);
  };

  const weekBadge = (w) => {
    if (w.has_results) return <span className="ro-sb-badge ro-sb-badge-success">Results</span>;
    if (w.has_distance_matrix) return <span className="ro-sb-badge ro-sb-badge-warn">Matrix</span>;
    if (w.has_addresses) return <span className="ro-sb-badge">Data</span>;
    return null;
  };

  return (
    <aside className="ro-sidebar">
      {/* Companies */}
      <div className="ro-sb-section">
        <div className="ro-sb-header">
          <span className="ro-sb-label">COMPANIES</span>
          <button className="ro-sb-add" onClick={() => { setAddingCompany(!addingCompany); setNewName(''); }}>+</button>
        </div>
        {addingCompany && (
          <form className="ro-sb-add-form" onSubmit={handleAddCompany}>
            <input autoFocus value={newName} onChange={e => setNewName(e.target.value)} placeholder="Company name" />
            <button type="submit">Add</button>
          </form>
        )}
        {[...companies].sort((a, b) => a.name.localeCompare(b.name)).map(c => (
          <div
            key={c.id}
            className={`ro-sb-item ${selectedCompany === c.id ? 'active' : ''}`}
            onClick={() => selectCompany(c.id)}
          >
            <span className="ro-sb-item-name">{c.name}</span>
            <button className="ro-sb-delete" onClick={(e) => handleDeleteCompany(e, c.id)}>&times;</button>
          </div>
        ))}
        {companies.length === 0 && <div className="ro-sb-empty">No companies</div>}
      </div>

      {/* Branches */}
      {selectedCompany && (
        <div className="ro-sb-section">
          <div className="ro-sb-header">
            <span className="ro-sb-label">BRANCHES</span>
            <button className="ro-sb-add" onClick={() => { setAddingBranch(!addingBranch); setNewName(''); }}>+</button>
          </div>
          {addingBranch && (
            <form className="ro-sb-add-form" onSubmit={handleAddBranch}>
              <input autoFocus value={newName} onChange={e => setNewName(e.target.value)} placeholder="Branch name" />
              <button type="submit">Add</button>
            </form>
          )}
          {[...branches].sort((a, b) => a.name.localeCompare(b.name)).map(b => (
            <div
              key={b.id}
              className={`ro-sb-item ${selectedBranch === b.id ? 'active' : ''}`}
              onClick={() => selectBranch(b.id)}
            >
              <span className="ro-sb-item-name">{b.name}</span>
              <span className="ro-sb-badge">{b.week_count} weeks</span>
              <button className="ro-sb-delete" onClick={(e) => handleDeleteBranch(e, b.id)}>&times;</button>
            </div>
          ))}
          {branches.length === 0 && <div className="ro-sb-empty">No branches</div>}
        </div>
      )}

      {/* Weekly Setups */}
      {selectedBranch && (
        <div className="ro-sb-section">
          <div className="ro-sb-header">
            <span className="ro-sb-label">WEEKLY SETUPS</span>
            <button className="ro-sb-add" onClick={() => { setAddingWeek(!addingWeek); setNewName(''); }}>+</button>
          </div>
          {addingWeek && (
            <form className="ro-sb-add-form" onSubmit={handleAddWeek}>
              <input autoFocus value={newName} onChange={e => setNewName(e.target.value)} placeholder="Week name" />
              <button type="submit">Add</button>
            </form>
          )}
          {[...weeks].sort((a, b) => a.name.localeCompare(b.name)).map(w => (
            <div
              key={w.id}
              className={`ro-sb-item ${selectedWeek === w.id ? 'active' : ''}`}
              onClick={() => selectWeek(w.id)}
            >
              <span className="ro-sb-item-name">{w.name}</span>
              {weekBadge(w)}
              <button className="ro-sb-delete" onClick={(e) => handleDeleteWeek(e, w.id)}>&times;</button>
            </div>
          ))}
          {weeks.length === 0 && <div className="ro-sb-empty">No weeks yet</div>}
        </div>
      )}

      {/* Quick Start at bottom */}
      {companies.length === 0 && (
        <div className="ro-sb-section ro-sb-quickstart">
          <div className="ro-sb-label">QUICK START</div>
          <button className="ro-sb-qs-btn" onClick={() => navigate('/routeoptimizer')}>
            Load Demo Data
          </button>
        </div>
      )}
    </aside>
  );
}
